// =============================================================================
// Language provider + useT hook
// =============================================================================
// Use:
//   const t = useT();
//   t("profile.title")                    -> "Profile" or "சுயவிவரம்"
//   t("billing.payPerYear", { amount: "Rs. 120" }) -> "Pay Rs. 120 / year"
//
// To change language, call setLanguage("en" | "ta"). The choice persists in
// localStorage as TICKANDPASS_LANG.
//
// Adding a new language: extend the LANGUAGES const and add the third key to
// every entry in strings.js. The infrastructure already supports it.
// =============================================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { strings } from "./strings";

const STORAGE_KEY = "TICKANDPASS_LANG";
export const LANGUAGES = ["en", "ta"];
const DEFAULT_LANG = "en";

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLanguage: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_LANG;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return LANGUAGES.includes(saved) ? saved : DEFAULT_LANG;
  });

  // Persist on change
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, lang);
      // Also update the <html lang> attribute for accessibility
      document.documentElement.setAttribute("lang", lang);
    }
  }, [lang]);

  const setLanguage = useCallback((next) => {
    if (LANGUAGES.includes(next)) setLang(next);
  }, []);

  /**
   * Translate a key. Optionally interpolate {placeholders} from a vars map.
   * Falls back to the key itself if the string is missing - which makes
   * untranslated strings visible during development.
   */
  const t = useCallback(
    (key, vars) => {
      const entry = strings[key];
      let text = entry?.[lang] ?? entry?.en ?? key;
      if (vars) {
        for (const [name, value] of Object.entries(vars)) {
          text = text.replace(new RegExp(`{${name}}`, "g"), String(value));
        }
      }
      return text;
    },
    [lang],
  );

  const value = useMemo(
    () => ({ lang, setLanguage, t }),
    [lang, setLanguage, t],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

/** Shorthand: just the translator function. */
export function useT() {
  return useContext(LanguageContext).t;
}
